import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarIcon, Clock, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

const productTypes = [
  { id: "infissi", name: "Infissi e Serramenti", quantityLabel: "Quantità", unit: "pezzi", type: "number", min: "1", step: "1" },
  { id: "porte", name: "Porte", quantityLabel: "Quantità", unit: "pezzi", type: "number", min: "1", step: "1" },
  { id: "cucine", name: "Cucine" },
  { id: "arredamento", name: "Arredamento" },
  { id: "pergole", name: "Pergole e Gazebo", quantityLabel: "Di che dimensione?", type: "text", placeholder: "Es: 3 x 4 metri" },
  { id: "parquet", name: "Parquet", quantityLabel: "Quanti metri quadrati?", unit: "m²", type: "number", min: "0.01", step: "0.01" },
  { id: "pavimenti", name: "Pavimenti", quantityLabel: "Quanti metri quadrati?", unit: "m²", type: "number", min: "0.01", step: "0.01" },
  { id: "outdoor", name: "Arredamento Outdoor" },
];

// Orari apertura: 9:00-13:00 e 16:00-19:00 (lunedì-venerdì)
const morningSlots = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30"];
const afternoonSlots = ["16:00", "16:30", "17:00", "17:30", "18:00", "18:30"];
const timeSlots = [...morningSlots, ...afternoonSlots];

// Giorni festivi italiani 2024-2026
const italianHolidays = [
  // 2024
  "2024-01-01", "2024-01-06", "2024-04-01", "2024-04-25", "2024-05-01",
  "2024-06-02", "2024-08-15", "2024-11-01", "2024-12-08", "2024-12-25", "2024-12-26",
  // 2025
  "2025-01-01", "2025-01-06", "2025-04-21", "2025-04-25", "2025-05-01",
  "2025-06-02", "2025-08-15", "2025-11-01", "2025-12-08", "2025-12-25", "2025-12-26",
  // 2026
  "2026-01-01", "2026-01-06", "2026-04-06", "2026-04-25", "2026-05-01",
  "2026-06-02", "2026-08-15", "2026-11-01", "2026-12-08", "2026-12-25", "2026-12-26",
];

// Funzione per verificare se un giorno è disabilitato
const isDateDisabled = (dateToCheck) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (dateToCheck < today) return true;
  
  const dayOfWeek = dateToCheck.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) return true;
  
  const dateStr = format(dateToCheck, "yyyy-MM-dd");
  return italianHolidays.includes(dateStr);
};

const PrenotazionePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availability, setAvailability] = useState({});
  const [availabilityError, setAvailabilityError] = useState("");
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);
  const [success, setSuccess] = useState(false);
  const [date, setDate] = useState(null);
  const bookingRequest = useRef(null);
  const [formData, setFormData] = useState({
    time: "",
    product_type: "",
    quantity: "",
    notes: ""
  });

  const selectedProduct = productTypes.find(p => p.name === formData.product_type);
  const selectedSlot = formData.time ? availability[formData.time] : null;

  useEffect(() => {
    if (!date || isDateDisabled(date) || !formData.product_type) {
      setCheckingAvailability(false);
      setAvailability({});
      setAvailabilityError("");
      return;
    }

    let cancelled = false;
    const dateStr = format(date, "yyyy-MM-dd");

    const fetchAvailability = async () => {
      setCheckingAvailability(true);
      setAvailability({});
      setFormData(current => ({ ...current, time: "" }));
      setAvailabilityError("");

      try {
        const results = await Promise.all(timeSlots.map(async (slot) => {
          try {
            const response = await axios.get(`${API}/appointments/check-availability`, {
              params: { date: dateStr, time: slot, product_type: formData.product_type },
              headers: { Authorization: `Bearer ${token}` }
            });
            return [slot, response.data];
          } catch (error) {
            return [slot, {
              available: false,
              detail: error.response?.data?.detail || "Disponibilita' non verificabile"
            }];
          }
        }));

        if (cancelled) return;

        const nextAvailability = Object.fromEntries(results);
        setAvailability(nextAvailability);

        const firstAvailable = results.find(([, value]) => value.available);
        setFormData((current) => (
          current.time && !nextAvailability[current.time]?.available
            ? { ...current, time: "" }
            : current
        ));
        if (!firstAvailable) {
          const firstError = results.find(([, value]) => value.detail)?.[1]?.detail;
          setAvailabilityError(firstError || "Nessun orario disponibile per questa data");
        }
      } catch {
        if (!cancelled) {
          setAvailability({});
          setAvailabilityError("Impossibile verificare la disponibilita' del calendario");
        }
      } finally {
        if (!cancelled) setCheckingAvailability(false);
      }
    };

    fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [date, token, formData.product_type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !formData.time || !formData.product_type) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (selectedProduct?.quantityLabel && !formData.quantity.trim()) {
      toast.error("Compila il campo " + selectedProduct.quantityLabel.toLowerCase());
      return;
    }
    if (selectedProduct?.type === "number" && (!Number.isFinite(Number(formData.quantity)) || Number(formData.quantity) <= 0 || (selectedProduct.unit === "pezzi" && !Number.isInteger(Number(formData.quantity))))) {
      toast.error(selectedProduct.unit === "pezzi" ? "Inserisci una quantità intera maggiore di zero" : "Inserisci una superficie maggiore di zero");
      return;
    }

    setLoading(true);
    try {
      if (selectedSlot && selectedSlot.available === false) {
        toast.error("Questo orario non e' disponibile");
        return;
      }

      const payload = {
        date: format(date, "yyyy-MM-dd"),
        time: formData.time,
        product_type: formData.product_type,
        quantity: selectedProduct?.quantityLabel ? formData.quantity.trim() : "",
        quantity_unit: selectedProduct?.unit || "",
        notes: formData.notes
      };
      const fingerprint = JSON.stringify(payload);
      if (!bookingRequest.current || bookingRequest.current.fingerprint !== fingerprint) bookingRequest.current = { fingerprint, id: crypto.randomUUID() };
      const response = await axios.post(`${API}/appointments`, payload, {
        headers: { Authorization: `Bearer ${token}`, "Idempotency-Key": bookingRequest.current.id }
      });
      
      setConfirmedAppointment(response.data);
      setSuccess(true);
      toast.success("Prenotazione effettuata con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la prenotazione");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="prenotazione-success">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle className="text-emerald-500" size={48} />
          </div>
          <h1 className="text-2xl font-light text-white">Prenotazione Confermata!</h1>
          <p className="text-white/60">
            Ti aspettiamo in showroom il {format(date, "d MMMM yyyy", { locale: it })} alle {formData.time}
          </p>
          <p className="text-sm text-emerald-500">
            L'appuntamento è stato aggiunto al calendario aziendale
          </p>
          {confirmedAppointment?.assigned_calendar && (
            <p className="text-xs text-white/50">
              Calendario assegnato: {confirmedAppointment.assigned_calendar}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/profile")}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Vedi Prenotazioni
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSuccess(false);
                setDate(null);
                setConfirmedAppointment(null);
                setFormData({ time: "", product_type: "", quantity: "", notes: "" });
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Nuova Prenotazione
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="prenotazione-page">
      {/* Header */}
      <div className="p-4 space-y-2 border-b border-white/10">
        <h1 className="text-2xl font-light text-white tracking-wide">Prenota Appuntamento</h1>
        <p className="text-sm text-white/60">Visita il nostro showroom</p>
        <p className="text-xs text-white/40">
          Aperti: Lun-Ven 9:00-13:00 e 16:00-19:00
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Date Picker */}
        <div className="space-y-2">
          <Label className="text-white/70">Data *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-12 justify-start text-left bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
                data-testid="date-picker"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-white/50" />
                {date ? format(date, "EEEE d MMMM yyyy", { locale: it }) : "Seleziona data"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
              <Calendar
                mode="single"
                selected={date}
              onSelect={setDate}
                locale={it}
                disabled={isDateDisabled}
                className="bg-zinc-900 text-white"
              />
              <div className="p-3 border-t border-zinc-800">
                <p className="text-xs text-white/40 text-center">
                  Chiusi: Sabato, Domenica e Festivi
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Time Select */}
        <div className="space-y-2">
          <Label className="text-white/70">Orario *</Label>
          <Select value={formData.time} onValueChange={(v) => setFormData({ ...formData, time: v })}>
            <SelectTrigger 
              className="h-12 bg-zinc-900 border-zinc-800 text-white"
              data-testid="time-select"
            >
              <Clock className="mr-2 h-4 w-4 text-white/50" />
              <SelectValue placeholder="Seleziona orario" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
              <SelectItem key="morning-header" value="__morning_header__" disabled className="text-emerald-500 font-medium text-xs">
                Mattina (9:00-13:00)
              </SelectItem>
              {morningSlots.map((slot) => (
                <SelectItem
                  key={`morning-${slot}`}
                  value={slot}
                  disabled={!date || checkingAvailability || availability[slot]?.available !== true}
                  className="text-white hover:bg-zinc-800 disabled:text-white/30"
                >
                  {slot}{availability[slot]?.available === false ? " - non disponibile" : ""}
                </SelectItem>
              ))}
              <SelectItem key="afternoon-header" value="__afternoon_header__" disabled className="text-emerald-500 font-medium text-xs border-t border-zinc-800 mt-1 pt-2">
                Pomeriggio (16:00-19:00)
              </SelectItem>
              {afternoonSlots.map((slot) => (
                <SelectItem
                  key={`afternoon-${slot}`}
                  value={slot}
                  disabled={!date || checkingAvailability || availability[slot]?.available !== true}
                  className="text-white hover:bg-zinc-800 disabled:text-white/30"
                >
                  {slot}{availability[slot]?.available === false ? " - non disponibile" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-white/40">
            {checkingAvailability
              ? "Verifica disponibilita' calendari in corso..."
              : availabilityError || "Orari disponibili: 9:00-13:00 e 16:00-19:00"}
          </p>
          {selectedSlot?.calendar?.name && (
            <p className="text-xs text-emerald-400">
              Questo orario sara' assegnato a: {selectedSlot.calendar.name}
            </p>
          )}
        </div>

        {/* Product Type */}
        <div className="space-y-2">
          <Label className="text-white/70">Cosa ti interessa vedere? *</Label>
          <Select value={formData.product_type} onValueChange={(v) => {
            setAvailability({});
            setFormData(current => ({ ...current, product_type: v, quantity: "", time: "" }));
          }}>
            <SelectTrigger 
              className="h-12 bg-zinc-900 border-zinc-800 text-white"
              data-testid="product-type-select"
            >
              <SelectValue placeholder="Seleziona categoria" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
              {productTypes.map((type) => (
                <SelectItem key={`product-${type.id}`} value={type.name} className="text-white hover:bg-zinc-800">
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Dynamic Quantity Field */}
        {selectedProduct?.quantityLabel && (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
            <Label htmlFor="booking-quantity" className="text-white/70">{selectedProduct.quantityLabel} *</Label>
            <div className="flex items-center gap-3">
              <Input
                id="booking-quantity"
                type={selectedProduct.type}
                min={selectedProduct.min}
                step={selectedProduct.step}
                required
                maxLength={150}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                placeholder={selectedProduct.placeholder || "Es: 5"}
                className="h-12 bg-zinc-900 border-zinc-800 text-white focus:border-emerald-500 flex-1"
                data-testid="quantity-input"
              />
              {selectedProduct.unit && <span className="text-white/50 text-sm w-16">{selectedProduct.unit}</span>}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="space-y-2">
          <Label className="text-white/70">Note aggiuntive</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Descrivi eventuali esigenze specifiche..."
            className="bg-zinc-900 border-zinc-800 text-white min-h-[100px] focus:border-emerald-500"
            data-testid="notes-textarea"
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading || checkingAvailability}
          className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full"
          data-testid="prenotazione-submit"
        >
          {loading ? "Prenotazione in corso..." : "Conferma Prenotazione"}
        </Button>
      </form>
    </div>
  );
};

export default PrenotazionePage;
